<template>
    <BaseSection>
        <template #title>
            <h1 v-if="error && error.statusCode === 404">{{ title }}</h1>
            <h1 v-else>{{ offlineTitle }}</h1>
        </template>
    </BaseSection>

    <template v-if="error.statusCode === 404">
        <p class="lead">
            {{ description }}
        </p>

        <p v-if="extraDescription && extraDescription !== '404-extra-description'">
            {{ extraDescription }}
        </p>

        <BaseSection>
            <QuicklinksList
                class="QuicklinksList--large"
                :links="[
                    { text: 'Search through our website', uri: '/search' },
                    { text: 'Go back to the homepage', uri: '/' },
                    { text: 'Find help on the contact page', uri: '/contact' },
                ]"
            />
        </BaseSection>
    </template>

    <template v-else>
        <p class="lead">
            Following a recently announced security issue affecting websites worldwide that use PHP, a widely used web
            technology, we have temporarily taken our website offline as a precaution. This allows us to apply the
            necessary updates and make sure everything is secure.
        </p>
        <p>Looking for the Guidelines? You can still access the full Guidelines through our Guidelines app:</p>
        <ul>
            <li>For iOS: EAU-Guidelines App - App Store</li>
            <li>For Android: EAU-Guidelines - Apps on Google Play</li>
        </ul>
        <p>
            We apologise for any inconvenience and expect to be back online shortly. Thank you for your patience.
        </p>
        <p>European Association of Urology</p>
    </template>
</template>

<script setup>
import { useSiteDataStore } from '~/store/siteDataStore';

const { activeSite, featureFlag } = useMultisite();
const { translations } = useSiteDataStore();

const localePath = useRoute().path?.split('/')?.filter(Boolean)?.[0];
let locale = localePath;

const offlineTitle = 'Temporary offline';

const title = ref('');
const description = ref('');
const extraDescription = ref('');

// Fix for Dutch locale, only supported locale that is more than 2 characters
if (locale === 'nl') {
    locale = 'nl-NL';
}

const category = 'site';

const getFallbackTranslation = (key) =>
    toValue(translations)?.[locale]?.[category]?.[key] ??
    toValue(translations)?.['en-US']?.[category]?.[key] ??
        key;

const props = defineProps({
    error: {
        type: Object,
        required: false,
        default: null
    }
});

if (props.error.statusCode === 404) {
    if (!featureFlag('custom-404-page')) {
        // If the custom 404 page feature flag is not enabled, 
        // show static, untranslated texts
        title.value = 'Page not found';
        description.value = 'This page could not be found. The link might be broken or the page moved.';
        extraDescription.value = '';
    } else if (toValue(activeSite).locale === 'en-US') {
        // If feature flag is enabled and the locale is set to English
        // it's possible that we're on a 404 page for a non-English site
        // For this case, we want to use the custom translation implementation
        title.value = getFallbackTranslation('404-title');
        description.value = getFallbackTranslation('404-description');
        extraDescription.value = getFallbackTranslation('404-extra-description');
    } else {
        // If the locale is not set to English, we can use the useTranslation() implementation
        const t = useTranslation('site');

        title.value = t('404-title');
        description.value = t('404-description');
        extraDescription.value = t('404-extra-description');
    }
} else {
    title.value = offlineTitle;
    description.value = 'We have temporarily taken our website offline as a precaution. We expect to be back online shortly.';
}

const data = computed(() => {
    return {
        title: title.value,
        description: description.value
    };
});

const head = useSeo(data, data, undefined, activeSite);

useHead(head);
</script>
